/**
 * Історія документа (2026-08-09, рішення власника): хто/коли/що зробив.
 *
 * Джерело — core.audit_log (DB-тригер на всіх головних документах, 0411).
 * Попап: дата — особа (email) — дія (створено/проведено/розпроведено/
 * змінено/видалено) — які поля змінені (старе → нове, текстом).
 * Кнопка рендериться DocumentToolbar поруч з «дт-кт» (той самий docRef).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import BodyPortal from './BodyPortal';
import type { DtKtDocRef } from './DtKtJournal';
import { relatedDocName } from './RelatedDocs';

interface HistoryChange { field: string; old: unknown; new: unknown }
interface HistoryItem {
  occurred_at: string;
  user_email: string | null;
  action: string;
  changes: HistoryChange[];
}

/** Людські назви частих полів документів (unknown → як є). */
const FIELD_LABELS: Record<string, string> = {
  document_number: '№ документа',
  document_date: 'дата документа',
  contractor_id: 'контрагент',
  contract_id: 'договір',
  warehouse_id: 'склад',
  comment: 'коментар',
  amount: 'сума',
  amount_without_vat: 'сума без ПДВ',
  vat_amount: 'сума ПДВ',
  total_amount: 'разом',
  vat_rate_id: 'ставка ПДВ',
  exchange_rate: 'курс',
  supplier_doc_number: '№ документа постачальника',
  supplier_doc_date: 'дата документа постачальника',
  invoice_number: '№ ПН',
  invoice_date: 'дата ПН',
  deletion_mark: 'позначка видалення',
  responsible: 'відповідальний',
  project_id: 'проект/напрям',
  settlement_account_code: 'рахунок розрахунків',
  corresponding_account: 'кореспондуючий рахунок',
  cash_account_code: 'рахунок грошових коштів',
  expense_type: 'вид витрати',
  distribute_by: 'база розподілу',
  customs_number: '№ ВМД',
  customs_date: 'дата ВМД',
  customs_fee: 'збір за оформлення',
  delivery_date: 'термін поставки',
  closing_status: 'статус закриття',
  erpn_status: 'статус ЄРПН',
  sign_status: 'статус підпису',
  total_cost: 'собівартість разом',
  posted: 'проведено',
  // ч.1120: документ «Закриття місяця».
  status: 'статус',
  period_year: 'рік періоду',
  period_month: 'місяць періоду',
  pre_check_passed: 'перевірку пройдено',
  // ч.1220: ручна привʼязка до іншого документа.
  linked_document: 'документ',
};

const ACTION_COLORS: Record<string, string> = {
  'створено': '#1d4ed8',
  'проведено': '#15803d',
  'розпроведено': '#b45309',
  'змінено': '#334155',
  'видалено': '#dc2626',
  'привʼязано документ': '#1d4ed8',
  'відвʼязано документ': '#b45309',
};

/** ч.1220: звʼязаний документ у записі історії — «Рахунок покупцю № 12 від 01.09.2026». */
function linkedDocLabel(v: { document_type: string; number?: string | null; date?: string | null;
  order_kind?: string | null }): string {
  const d = v.date ? new Date(v.date) : null;
  const day = d && !Number.isNaN(d.getTime()) ? ` від ${d.toLocaleDateString('uk-UA')}` : '';
  return `${relatedDocName(v)} № ${v.number ?? '—'}${day}`;
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object' && typeof (v as { document_type?: unknown }).document_type === 'string') {
    return linkedDocLabel(v as Parameters<typeof linkedDocLabel>[0]);
  }
  if (v === true) return 'так';
  if (v === false) return 'ні';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return new Date(s).toLocaleString('uk-UA');
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(s)) return s.slice(0, 8) + '…';
  return s.length > 60 ? s.slice(0, 60) + '…' : s;
}

export function DocHistoryButton({ doc }: { doc: DtKtDocRef }) {
  const [open, setOpen] = useState(false);
  const docId = doc.id && doc.id !== 'new' ? doc.id : undefined;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={!docId}
        title={docId
          ? 'Хто і коли створив, змінював, проводив документ'
          : 'Спершу збережіть документ'}>
        історія
      </button>
      {open && docId && (
        <HistoryModal docType={doc.type} docId={docId} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

export function HistoryModal({ docType, docId, onClose }: {
  docType: string; docId: string; onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['doc-history', docType, docId],
    queryFn: () =>
      api.get<{ items: HistoryItem[] }>('/documents/audit-history', {
        params: { document_type: docType, document_id: docId },
      }).then((r) => r.data),
  });

  return (
    <BodyPortal>
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div className="card" style={{ width: 'min(860px, 94vw)', maxHeight: '80vh',
        overflowY: 'auto', margin: 0 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Історія документа</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>

        {q.isLoading && <p style={{ color: 'var(--muted)' }}>Завантаження…</p>}
        {q.isError && <div className="error">Не вдалося завантажити історію</div>}
        {q.data && q.data.items.length === 0 && (
          <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
            Записів немає — документ створено до впровадження журналу історії.
          </p>
        )}

        {q.data && q.data.items.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
                <th style={{ padding: '0.45rem', width: 150 }}>Дата</th>
                <th style={{ padding: '0.45rem', width: 220 }}>Особа</th>
                <th style={{ padding: '0.45rem', width: 120 }}>Дія</th>
                <th style={{ padding: '0.45rem' }}>Що змінено</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((it, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
                  <td style={{ padding: '0.45rem', whiteSpace: 'nowrap' }}>
                    {new Date(it.occurred_at).toLocaleString('uk-UA')}
                  </td>
                  <td style={{ padding: '0.45rem' }}>
                    {it.user_email ?? <span style={{ color: 'var(--muted)' }}>система</span>}
                  </td>
                  <td style={{ padding: '0.45rem' }}>
                    <span style={{ color: ACTION_COLORS[it.action] ?? '#334155', fontWeight: 600 }}>
                      {it.action}
                    </span>
                  </td>
                  <td style={{ padding: '0.45rem', color: 'var(--text-secondary)' }}>
                    {it.changes.length === 0 ? '' : it.changes.map((c, j) => (
                      <div key={j}>
                        {FIELD_LABELS[c.field] ?? c.field}:{' '}
                        <span style={{ color: '#94a3b8' }}>{fmtVal(c.old)}</span>
                        {' → '}
                        <b>{fmtVal(c.new)}</b>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </BodyPortal>
  );
}
